import { useEffect, useState } from "react";
import { animationFrameScheduler, concatMap, interval, map, Observable, of } from "rxjs";

import { getPartyAudioTrack } from "./track";

type Room = import("twilio-video").Room;
type Party = {
  audioFeed: import("twilio-video").Participant;
  videoFeed: import("twilio-video").Participant;
  id: string;
};

type TrackStats =
  | import("twilio-video").LocalAudioTrackStats
  | import("twilio-video").RemoteAudioTrackStats;

type VolumeLevel = Map<string, [string, Observable<number> | null]>;

function useParticipantVolume(room: Room | null, parties: Party[]) {
  const [volumeLevels, setVolumeLevels] = useState<VolumeLevel>(new Map());

  function createVolumeLevel$(party: Party): [string, Observable<number> | null] {
    const audioTrack = getPartyAudioTrack(party);
    if (!room || !audioTrack) {
      return ["", of(0)];
    }

    const partyAudioTrackId = audioTrack.trackSid;

    const volumeLevel$ = interval(0, animationFrameScheduler)
      .pipe(
        concatMap(async () => {
          const statsReport = await room.getStats();
          if (statsReport.length > 0) {
            return statsReport[0];
          }
          return null;
        }),
      )
      .pipe(
        map((roomStats) => {
          if (!roomStats) {
            return 0;
          }
          let audioStats = roomStats.localAudioTrackStats as TrackStats[];
          if (roomStats.remoteAudioTrackStats.length > 0) {
            audioStats = audioStats.concat(roomStats.remoteAudioTrackStats);
          }
          const partyVolumeStat = audioStats.find((stat) => stat.trackSid === partyAudioTrackId);
          if (partyVolumeStat?.audioLevel) {
            // normalize into a volume percentage of 0 - 100
            const normalizedVolume = Math.round((partyVolumeStat.audioLevel / 32767) * 100);

            if (normalizedVolume < 4) {
              // treat less than x% as 0% to remove silent jitter
              return 0;
            }

            // Use a gain to get a higher percentage when talking normally.
            return Math.min(normalizedVolume * 1.3, 100);
          }
          return 0;
        }),
      );
    return [partyAudioTrackId, volumeLevel$];
  }

  const partyAudioFeedIds = parties.map((p) => getPartyAudioTrack(p)?.trackSid).join();
  useEffect(() => {
    setVolumeLevels((oldVolumeLevels) => {
      return parties.reduce((accum, party): VolumeLevel => {
        const volumeLevel = oldVolumeLevels.get(party.id);
        let latestVolumeLevel$ = volumeLevel?.[1];
        let latestTrackId = getPartyAudioTrack(party)?.trackSid;
        if (volumeLevel?.[0] !== latestTrackId) {
          [latestTrackId, latestVolumeLevel$] = createVolumeLevel$(party);
        }

        const newVolumeLevels = new Map(accum.entries());
        newVolumeLevels.set(party.id, [latestTrackId, latestVolumeLevel$]);
        return newVolumeLevels;
      }, new Map());
    });
  }, [partyAudioFeedIds, room]);

  return Array.from(volumeLevels.keys()).reduce((accum, partyId) => {
    const volumeLevel = volumeLevels.get(partyId);
    return {
      ...accum,
      [partyId]: volumeLevel?.[1],
    };
  }, {});
}

export default useParticipantVolume;
